import { Injectable } from '@angular/core';
import { ApiService } from '@services/api.service';

import {
  DeviceNameUpdateRequest,
  DeviceSettingTagUpdateRequest,
  DeviceSettingUpdateRequest,
  DeviceSettingResponse,
  DeviceTagResponse,
  DeviceSettingGroupResponse,
  DeviceGroupSettingResponse,
  DeviceSettingGroupUpdateRequest,
  DeviceResponse,
  GroupDeviceResponse,
} from '@entities/deviceSetting';
import { DeviceSettingListResponse } from '@entities/deviceSetting';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DeviceSettingService {
  constructor(private api: ApiService) {}

  /**
   * デバイスのトラッカーを取得する
   * @returns トラッカー情報
   */
  find(serialNo: string): Observable<DeviceSettingResponse> {
    const url = `/trackers/${serialNo}`;
    return this.api.get<DeviceSettingResponse>(url);
  }

  /**
   * デバイス設定一覧を取得する
   * @returns デバイス設定一覧
   */
  findAll(): Observable<DeviceSettingListResponse[]> {
    const url = '/device-settings';
    return this.api.get<DeviceSettingListResponse[]>(url);
  }

  /**
   * デバイスタグ情報を取得する
   * @returns デバイスタグ情報
   */
  findTagList(serialNoList: string[]): Observable<DeviceTagResponse> {
    const url = `/tags/_get`;
    return this.api.post<DeviceTagResponse>(url, serialNoList);
  }

  /**
   * デバイス設定グループを取得する
   * @returns デバイス設定グループ情報
   */
  findDeviceSettingGroupList(
    skipLoading = false,
  ): Observable<DeviceSettingGroupResponse[]> {
    const url = `/groups`;
    return this.api.get<DeviceSettingGroupResponse[]>(
      url,
      undefined,
      skipLoading,
    );
  }

  /**
   * グループに無所属のデバイス一覧を取得する
   * @returns デバイス一覧
   */
  findDeviceList(): Observable<DeviceResponse[]> {
    const url = `/devices?nogroup=true`;
    return this.api.get<DeviceResponse[]>(url);
  }

  /** 対象のグループとそれに所属するデバイス一覧を取得する。
   * @returns デバイス一覧
   */
  findGroupDeviceList(groupId?: string): Observable<GroupDeviceResponse> {
    const url = `/devices/groups/${groupId}`;
    return this.api.get<GroupDeviceResponse>(url);
  }

  /**
   * グループ化されたデバイスのトラッカーを取得する。（一括設定時の初期表示データ取得）
   * @returns トラッカー情報
   */
  findDeviceGroupSetting(
    groupId: string,
    deviceTypeKey: string,
  ): Observable<DeviceGroupSettingResponse> {
    const url = `/trackers/bulk/${groupId}?device_type_key=${deviceTypeKey}`;
    return this.api.get<DeviceGroupSettingResponse>(url);
  }

  /** デバイス設定グループを新規登録する */
  addDeviceSettingGroup(
    request: DeviceSettingGroupUpdateRequest,
  ): Observable<void> {
    const url = `/devices/groups`;
    return this.api.post<void>(url, request);
  }

  /**
   * トラッカーを更新する
   * @param request 登録内容
   */
  update(
    serialNo: string,
    request: DeviceSettingUpdateRequest,
  ): Observable<void> {
    const url = `/trackers/${serialNo}/config`;
    return this.api.put<void>(url, request);
  }

  /** デバイス名だけを更新する */
  updateName(
    serialNo: string,
    request: DeviceNameUpdateRequest,
  ): Observable<void> {
    return this.api.put<void>(`/devices/${serialNo}/name`, request);
  }

  /**
   * デバイス関連タグ更新する
   * @param request 登録内容
   */
  updateTag(request: DeviceSettingTagUpdateRequest): Observable<void> {
    const url = '/devices/tags/relations';
    return this.api.put<void>(url, request);
  }

  /** デバイス設定グループを更新する */
  updateDeviceSettingGroup(
    group_id: string,
    request: DeviceSettingGroupUpdateRequest,
  ): Observable<void> {
    const url = `/devices/groups/${group_id}`;
    return this.api.put<void>(url, request);
  }

  /**
   * グループに紐づくデバイスのトラッカーを更新する。（一括設定時の更新）
   * @param request 登録内容
   */
  updateGroupedSetting(
    groupId: string,
    request: DeviceSettingUpdateRequest,
  ): Observable<void> {
    const url = `/trackers/bulk/${groupId}/config`;
    return this.api.put<void>(url, request);
  }

  /**
   * 設定グループを削除する
   * @param groupId 設定グループID
   */
  deleteDeviceSettingGroup(groupId: string): Observable<void> {
    const url = `/devices/groups/${groupId}`;
    return this.api.delete<void>(url);
  }
}
